import { lazy, Suspense } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { DefaultElementProps } from 'common/interfaces'
import Loading from 'components/Loading'
import { SizingContainer } from 'components/SizingContainer'
import TextFieldOffline from 'components/TextFieldOffline'
import { useIsOnline } from 'utils/hook'
import { validationsToMapRules } from 'utils/validationsToRules'

import { MapAddress as MapAddressProps } from './interface'
import { MapAddressState } from './stateInterface'

const MapAddressGoogleMap = lazy(async () => {
  const result = await import('./components/MapAddressGoogleMap')
  return result
})

export default function MapAddress({
  name,
  label,
  onFocus,
  onChange,
  onInteraction,
  placeholder = 'Street, City, State, Zip',
  validations,
  disableFirstCurrentLocation = false,
}: MapAddressProps & DefaultElementProps): JSX.Element {
  const isOnline = useIsOnline()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: {},
    rules: validationsToMapRules(validations),
  })

  if (!isOnline) {
    return <TextFieldOffline label={label} field={field} />
  }

  const handleChange = (newValue: MapAddressState): void => {
    field.onChange(newValue)
    onChange?.({ value: newValue })
  }

  return (
    <SizingContainer className="map-address-wrap">
      <Suspense fallback={<Loading />}>
        <MapAddressGoogleMap
          required={validations?.required?.value}
          onFocus={onFocus}
          onChange={handleChange}
          label={label}
          fieldValue={field.value}
          placeholder={placeholder}
          onInteraction={onInteraction}
          error={error}
          disableFirstCurrentLocation={disableFirstCurrentLocation}
        />
      </Suspense>
    </SizingContainer>
  )
}
